import { type RefObject, useEffect, useRef } from 'react';

interface Options {
    requireFullyVisible?: boolean;
}

export function useOnVisible<T extends Element>(
    onVisible: () => void,
    { requireFullyVisible = false }: Options = {},
): RefObject<T> {
    const ref = useRef<T>(null);
    const callback = useRef(onVisible);
    callback.current = onVisible;

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        if (typeof IntersectionObserver === 'undefined') {
            callback.current();
            return;
        }

        let fired = false;
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting && !fired) {
                        fired = true;
                        callback.current();
                        observer.disconnect();
                    }
                }
            },
            /* Subpixel layout means a fully visible element can report a ratio just below 1. */
            { threshold: requireFullyVisible ? 0.99 : 0 },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [requireFullyVisible]);

    return ref;
}
